import {useCallback} from "react"
import type {CropParams, Dimensions, Position} from "../types/types"

interface CropRenderParams {
    mainCtx: CanvasRenderingContext2D
    img: HTMLImageElement | HTMLVideoElement
    position: Position
    dimensions: Dimensions
    cropDimensions: Dimensions
    cropParams: {
        cropX: number
        cropY: number
        scaledCropWidth: number
        scaledCropHeight: number
    }
    setCropParams: (params: CropParams) => void
}

export const useCropMode = () => {
    const renderCrop = useCallback((params: CropRenderParams) => {
        const {
            mainCtx,
            img,
            position,
            dimensions,
            cropParams,
            cropDimensions,
            setCropParams,
        } = params
        const sourceWidth =
            img instanceof HTMLVideoElement ? img.videoWidth : img.width
        const sourceHeight =
            img instanceof HTMLVideoElement ? img.videoHeight : img.height

        if (sourceWidth === 0 || sourceHeight === 0) {
            console.warn("Source has zero dimensions:", {
                isVideo: img instanceof HTMLVideoElement,
                sourceWidth,
                sourceHeight,
                readyState:
                    img instanceof HTMLVideoElement ? img.readyState : "N/A",
            })
            return
        }

        mainCtx.clearRect(0, 0, mainCtx.canvas.width, mainCtx.canvas.height)

        mainCtx.globalAlpha = 0.5
        mainCtx.drawImage(
            img,
            position.x,
            position.y,
            dimensions.width,
            dimensions.height
        )

        mainCtx.globalCompositeOperation = "destination-out"
        mainCtx.fillStyle = "black"
        mainCtx.fillRect(
            cropParams.cropX,
            cropParams.cropY,
            cropParams.scaledCropWidth,
            cropParams.scaledCropHeight
        )
        mainCtx.globalCompositeOperation = "source-over"
        mainCtx.globalAlpha = 1
        mainCtx.save()
        mainCtx.beginPath()
        mainCtx.rect(
            cropParams.cropX,
            cropParams.cropY,
            cropParams.scaledCropWidth,
            cropParams.scaledCropHeight
        )
        mainCtx.clip()
        mainCtx.drawImage(
            img,
            position.x,
            position.y,
            dimensions.width,
            dimensions.height
        )
        mainCtx.restore()

        mainCtx.strokeStyle = "black"
        mainCtx.lineWidth = 2
        mainCtx.strokeRect(
            cropParams.cropX,
            cropParams.cropY,
            cropParams.scaledCropWidth,
            cropParams.scaledCropHeight
        )

        const scale = dimensions.width / sourceWidth

        const sourceX = (cropParams.cropX - position.x) / scale
        const sourceY = (cropParams.cropY - position.y) / scale
        const sourceCropWidth = cropParams.scaledCropWidth / scale
        const sourceCropHeight = cropParams.scaledCropHeight / scale

        const calculatedParams = {
            offsetX: sourceX / sourceWidth,
            offsetY: sourceY / sourceHeight,
            repeatX: sourceCropWidth / sourceWidth,
            repeatY: sourceCropHeight / sourceHeight,
            cropWidth: cropDimensions.width,
            cropHeight: cropDimensions.height,
            sourceX,
            sourceY,
            sourceCropWidth,
            sourceCropHeight,
            sourceWidth,
            sourceHeight,
            mode: "crop" as const,
        }

        setCropParams(calculatedParams)
    }, [])

    return {renderCrop}
}
